import {
    useCapabilityIdsByCapabilityName,
    useDefinitionsActivitiesAndCategoriesQuery,
    useGroupsQuery,
    useSubmissionsQuery,
    useTemplatesQuery,
} from "@Components/shared/queries";
import { QueryKeys, ThreeMinutes } from "@Constants";
import SessionManager from "@Core/session";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IAssessmentDetails, IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { ITaskDetails } from "@Models/ITaskDetails";
import { IUserDetails, IUserMinimal } from "@Models/IUserDetails";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import ActivityService from "@Services/ActivityService";
import AssessmentService from "@Services/AssessmentService";
import UserGroupService from "@Services/UserGroupService";
import {
    DateTime,
    arrayToMapByKey,
    filterXOfObjectsFromArray,
    getCharacterSumHash,
    sortObjectByKey,
    uniqueArrayOfObjectsByKey,
} from "@Utils";
import { useQuery } from "@tanstack/react-query";
import { notification } from "antd";
import { useEffect } from "react";

const QUERY_SETTINGS = {
    staleTime: ThreeMinutes,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
};

export const useAssessmentsQuery = (userId = SessionManager.getServiceUser()?.userId) => {
    const assessments = useQuery<IAssessmentDetails[]>(
        [QueryKeys.AssessmentList, userId],
        async () => {
            const service = new AssessmentService();

            if (!userId) return null;

            const result = await service.getAll(userId).then((res) => {
                if (!res.errors.length) {
                    return res.value?.assessments;
                } else {
                    // res.errors;
                    // todo error handling
                    return [];
                }
            });
            return result;
        },
        {
            ...QUERY_SETTINGS,
            enabled: !!userId,
            placeholderData: [],
        }
    );

    useEffect(() => {
        // TODO: use error boundaries

        if (assessments.error) {
            notification.error({ message: "Something went wrong." });
        }
    }, [assessments.error]);

    return assessments;
};

export const useAssessmentQuery = (assessmentId: string, userId: string = undefined) => {
    const { data: assessments, ...otherProps } = useAssessmentsQuery(!!assessmentId ? userId : "");

    const assessment = assessments?.find((a) => a.assessmentId === assessmentId);

    return {
        data: assessment,
        ...otherProps,
    };
};

export const useAssessmentsInstancesQuery = (assessmentId: IAssessmentDetails["assessmentId"]) => {
    const assessmentInstances = useQuery<IAssessmentInstanceDetails[]>(
        [QueryKeys.AssessmentInstanceList, assessmentId],
        async () => {
            const service = new AssessmentService();
            return await service.getInstances(assessmentId).then((res) => {
                if (!res.errors.length) {
                    return res.value.assessmentInstances;
                } else {
                    notification.error({ message: "Something went wrong." });
                    return [];
                }
            });
        },
        {
            ...QUERY_SETTINGS,
            enabled: !!assessmentId,
            placeholderData: [],
        }
    );

    useEffect(() => {
        // TODO: use error boundaries

        if (assessmentInstances.error) {
            notification.error({ message: "Something went wrong." });
        }
    }, [assessmentInstances.error]);

    return assessmentInstances;
};

export const useUserAssessmentsInstancesQuery = (userId: string) => {
    const service = new AssessmentService();
    return useQuery<IAssessmentInstanceDetails[]>(
        [QueryKeys.AssessmentUserInstanceList, userId],
        async () => {
            return await service.getUserInstances(userId).then((res) => {
                if (!res.errors.length) {
                    return res.value;
                } else {
                    notification.error({ message: "Something went wrong." });
                    return [];
                }
            });
        },
        {
            ...QUERY_SETTINGS,
            placeholderData: [],
            enabled: !!userId,
        }
    );
};

export const useUserActivityInstancesQuery = (
    userId: string,
    activitiesIds: string[] = [],
    dateFrom: string,
    dateTo: string
) => {
    const activityService = new ActivityService();
    const { data: activityInstances, ...otherProps } = useQuery<IActivityDetails[]>(
        [QueryKeys?.ActivityListUser, userId, activitiesIds, dateFrom, dateTo, "desc"],
        async () => {
            return await activityService.getUser([userId], activitiesIds, [], dateFrom, dateTo, -1).then((res) => {
                if (!res.errors.length) {
                    // descending order
                    return res.value?.activities.sort((a, b) => +new Date(b.creationDate) - +new Date(a.creationDate));
                }
                return [];
            });
        },
        {
            ...QUERY_SETTINGS,
            enabled: !!userId && !!dateFrom && !!dateTo,
            placeholderData: [],
        }
    );

    return {
        activityInstances,
        ...otherProps,
    };
};

type ITaskByActivityId = Map<
    ITaskDetails["activityId"],
    Pick<ITaskDetails, "institutional" | "quantity" | "recorded" | "taskId">
>;

interface IUseTaskAssessmentsResponse {
    activityInstances: IActivityDetails[];
    isFetched: boolean;
    isFetching: boolean;
    isLoading: boolean;
    preCheckedInstances: Map<ITaskDetails["activityId"], Set<IActivityDetails["activityInstanceId"]>>;
    taskByActivityId: ITaskByActivityId;
    taskDetails: (ITaskDetails & { activityLabel: string })[];
}

export const useTaskAssessments = (
    userId: string,
    assessmentInstance: IAssessmentInstanceDetails
): IUseTaskAssessmentsResponse => {
    const taskDetails = assessmentInstance?.taskDetails ?? [];
    const activityIds = taskDetails.map((task) => task.activityId) ?? [];
    const endDate = assessmentInstance
        ? DateTime(assessmentInstance.endDate).addDays(assessmentInstance.lateSubmissions)
        : "";

    const startDate = assessmentInstance?.startDate ? DateTime(assessmentInstance.startDate).startOfDay() : "";

    const {
        activityInstances,
        isFetched: areUserActivityInstancesFetched,
        isFetching: areUserActivityInstancesFetching,
        isLoading: areUserActivityInstancesLoading,
    } = useUserActivityInstancesQuery(userId, activityIds, startDate, endDate !== "" ? endDate : undefined);

    const {
        activitiesMap,
        isFetched: isActivitiesMapFetched,
        isFetching: isActivitiesMapFetching,
        isLoading: isActivitiesMapLoading,
        ...otherProps
    } = useDefinitionsActivitiesAndCategoriesQuery();

    const preCheckedInstances = new Map();
    const taskByActivityId: ITaskByActivityId = new Map();

    const details = taskDetails.map((task) => {
        const { activityId, automated, recorded, institutional, taskId, quantity } = task;
        const isRecorded = recorded ?? false;
        if (automated) {
            const preCheckedAssessmentInstances = filterXOfObjectsFromArray(
                // @ts-expect-error This error will be resolve when we use strct types
                activityInstances,
                (item) => {
                    const isActivity = item.activityId === activityId;
                    return isRecorded ? item.mediaUrl && isActivity : isActivity;
                },
                quantity
            );

            preCheckedInstances.set(
                activityId,
                new Set(preCheckedAssessmentInstances.map((a) => a.activityInstanceId))
            );
        }
        taskByActivityId.set(activityId, { recorded, institutional, taskId, quantity });
        return { ...task, activityLabel: activitiesMap?.get(activityId).label, endDate: assessmentInstance?.endDate };
    });

    return {
        ...otherProps,
        activityInstances,
        isFetched: isActivitiesMapFetched && areUserActivityInstancesFetched,
        isFetching: areUserActivityInstancesFetching || areUserActivityInstancesFetching,
        isLoading:
            !isActivitiesMapFetched ||
            !areUserActivityInstancesFetched ||
            areUserActivityInstancesLoading ||
            isActivitiesMapLoading,
        preCheckedInstances,
        taskByActivityId,
        taskDetails: details,
    };
};

export const useUserActivitiesInstancesMap = (userId: string, assessmentInstance: IAssessmentInstanceDetails) => {
    const taskDetails = assessmentInstance?.taskDetails ?? [];
    const activityIds = taskDetails.map((task) => task.activityId) ?? [];
    const endDate = assessmentInstance
        ? DateTime(assessmentInstance.endDate).addDays(assessmentInstance.lateSubmissions)
        : "";
    const startDate = assessmentInstance?.startDate ? new Date(assessmentInstance.startDate)?.toISOString() : "";

    const { activityInstances, ...otherProps } = useUserActivityInstancesQuery(
        !!assessmentInstance ? userId : "", // stop the query from running if there is no assessmentInstance
        activityIds,
        startDate,
        endDate !== "" ? endDate : undefined
    );

    const activitiesInstancesMap = arrayToMapByKey(activityInstances, (ai) => ai.activityInstanceId, true);

    return {
        data: activitiesInstancesMap,
        ...otherProps,
    };
};

export const useActivitiesInstancesMap = (activityInstanceIds: IActivityDetails["activityInstanceId"][] = []) => {
    const { data: activityInstances, ...otherProps } = useSubmissionsQuery(activityInstanceIds);

    const activitiesInstancesMap: Map<string, IActivityDetails> = arrayToMapByKey(
        activityInstances,
        (ai) => ai.activityInstanceId,
        true
    );

    return {
        data: activitiesInstancesMap,
        ...otherProps,
    };
};

export const useTemplatesMap = (groupIds?: string[]) => {
    const templates = useTemplatesQuery(groupIds);
    const { data, ...otherProps } = templates;

    const usersMap = arrayToMapByKey(data, (u) => u.templateId, true);

    return {
        ...otherProps,
        data: usersMap,
    };
};

interface IUseUsersWithGroupIds {
    groupIds: IUserGroupDetails["groupId"][];
    userId?: undefined;
}
interface IUseUsersWIthUserId {
    groupIds?: undefined;
    userId?: IUserDetails["userId"];
}

/**
 *
 * @param userId required unless groupIds
 * @param groupIds required unless userId
 */
export const useUsersQuery = ({
    userId = SessionManager.getServiceUser()?.userId,
    groupIds,
}: IUseUsersWithGroupIds | IUseUsersWIthUserId = {}) => {
    const userGroupService = new UserGroupService();

    // this hook does not actually fetch if groupIds is passed in;
    const { data: groups } = useGroupsQuery(groupIds ? "" : userId);

    const ids = !!groupIds ? groupIds : groups.map((group) => group.groupId);

    return useQuery<IUserDetails[]>(
        [QueryKeys.GroupMembers, ids],
        async () => {
            return await userGroupService.getMembers(ids, -1).then((res) => {
                if (!res.errors.length) {
                    return res.value.users;
                }
                return [];
            });
        },
        {
            ...QUERY_SETTINGS,
            enabled: ids?.length > 0,
            placeholderData: [],
        }
    );
};

export const useUsersMap = ({
    userId = SessionManager.getServiceUser()?.userId,
    groupIds,
}: IUseUsersWithGroupIds | IUseUsersWIthUserId = {}) => {
    const obj = (!!userId && { userId }) || (!!groupIds && { groupIds }) || {};
    const users = useUsersQuery(obj);
    const { data, ...otherProps } = users;

    const usersMap = arrayToMapByKey(data, (u) => u.userId, true);

    return {
        data: usersMap,
        ...otherProps,
    };
};

const useAssessorsAndUserIdsByGroup = (groupIds: IUserGroupDetails["groupId"][]) => {
    const userGroupService = new UserGroupService();
    const assessorCapabilityIds = useCapabilityIdsByCapabilityName(["Assessment/Create", "Feedback/CreateTemplate"]);
    const studentCapabilityIds = useCapabilityIdsByCapabilityName(["Assessment/Submit"]);

    return useQuery<{
        assessors: IUserMinimal[];
        groupIdsByUser: Map<string, Set<IUserGroupDetails["groupId"]>>;
        userIdsByGroup: Map<string, Set<IUserMinimal["userId"]>>;
    }>(
        [QueryKeys.GroupMembersFiltered, getCharacterSumHash(groupIds)],
        async () => {
            const assessorIdsByGroup = new Map();
            const userIdsByGroup = new Map();
            const groupIdsByUser: Map<string, Set<IUserGroupDetails["groupId"]>> = new Map();

            // assessors are users with the capability to create assessments or feedback templates
            const assessorPromises = groupIds.map(async (groupId) => {
                return await userGroupService.getFilteredMembers(groupId, assessorCapabilityIds).then((res) => {
                    if (!res.value?.length) return null;
                    const assessorIds = res.value.map((value) => value.userId);
                    assessorIdsByGroup.set(groupId, new Set(assessorIds));
                    return res.value;
                });
            });

            // students are users with the capability to submit assessments
            const studentPromises = groupIds.map(async (groupId) => {
                return await userGroupService.getFilteredMembers(groupId, studentCapabilityIds).then((res) => {
                    if (!res.value?.length) return null;
                    const userIds = res.value.map((value) => {
                        if (!groupIdsByUser.has(value.userId)) groupIdsByUser.set(value.userId, new Set());
                        groupIdsByUser.get(value.userId).add(groupId);
                        return value.userId;
                    });
                    userIdsByGroup.set(groupId, new Set(userIds));
                    // we don't need to return anything here
                });
            });

            return await Promise.all([assessorPromises, studentPromises].flat()).then((assessors) => {
                const allAssessors = assessors.flat().filter((v) => v);
                const uniqueAssessors = uniqueArrayOfObjectsByKey(allAssessors, "userId");
                return {
                    assessors: sortObjectByKey(uniqueAssessors, "fullName"),
                    groupIdsByUser,
                    userIdsByGroup,
                };
            });
        },
        {
            ...QUERY_SETTINGS,
            enabled: !!SessionManager.getServiceUser() && groupIds?.length > 0,
            placeholderData: { assessors: [], groupIdsByUser: new Map(), userIdsByGroup: new Map() },
        }
    );
};

export const useAssessorAndGroupsQuery = (userId: string) => {
    const { data: groups } = useGroupsQuery(userId);

    const groupIds = groups.map((group) => group.groupId);

    const { data: usersMap, isFetched: isUsersMapFetched } = useUsersMap({ groupIds });
    const { data: templates, isFetched: areTemplatesFetched } = useTemplatesQuery(groupIds);

    const {
        data: { assessors, groupIdsByUser, userIdsByGroup },
        isFetched: areAssessorsAndUserIdsByGroupFetched,
        ...otherProps
    } = useAssessorsAndUserIdsByGroup(groupIds);

    return {
        ...otherProps,
        assessors,
        groupIds,
        groupIdsByUser,
        groups,
        isFetched: areAssessorsAndUserIdsByGroupFetched && isUsersMapFetched && areTemplatesFetched,
        templates,
        userIdsByGroup,
        usersMap,
    };
};
